<template>
  <div class="message-wrapper" :class="{'message-right': isUser, 'message-left': !isUser}">
    <div class="message-bubble" :class="{'theme--dark': $store.state.darkTheme, 'theme--light': !$store.state.darkTheme}" :style="bubbleStyle">
      <div class="message-content" :class="contentClass" v-html="content"></div>
      <div class="message-footer">
        <span class="message-time">{{ time }}</span>
        <span v-if="tokens" class="message-tokens">{{ tokens }} tokens</span>

        <!-- Copy button for assistant messages -->
        <v-btn
          v-if="!isUser"
          icon
          small
          class="copy-btn ml-auto"
          @click="copyToClipboard"
          :title="copied ? 'Copied!' : 'Copy message'"
        >
          <v-icon small>{{ copied ? 'check' : 'content_copy' }}</v-icon>
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MessageBubble',
  props: {
    content: {
      type: String,
      required: true
    },
    rawContent: {
      type: String,
      default: ''
    },
    isUser: {
      type: Boolean,
      default: false
    },
    time: {
      type: String,
      default: ''
    },
    tokens: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      copied: false
    }
  },
  computed: {
    bubbleStyle() {
      if (this.isUser) {
        return {
          background: 'var(--v-primary-base)',
          color: 'white'
        };
      } else {
        // Use the same CSS variables as the rest of the app
        // In light mode, use a darker text color for better contrast
        const isDark = this.$store.state.darkTheme;
        return {
          background: 'var(--card-back)',
          color: isDark ? 'var(--on-surface)' : 'rgba(0, 0, 0, 0.87)'
        };
      }
    },
    contentClass() {
      return this.isUser ? 'user-content' : 'assistant-content markdown-content';
    }
  },
  methods: {
    async copyToClipboard() {
      try {
        // Find the message content div
        const contentDiv = this.$el.querySelector('.message-content');

        if (!contentDiv) {
          throw new Error('Content div not found');
        }

        // Make content editable temporarily
        contentDiv.contentEditable = 'true';

        // Select all content
        const range = document.createRange();
        range.selectNodeContents(contentDiv);
        const selection = window.getSelection();
        selection.removeAllRanges();
        selection.addRange(range);

        // Copy to clipboard (this will preserve formatting)
        document.execCommand('copy');

        // Deselect
        selection.removeAllRanges();

        // Make it non-editable again
        contentDiv.contentEditable = 'false';

        this.copied = true;

        // Reset after 2 seconds
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy text:', err);
        // Fallback to plain text if the fancy method fails
        this.copyPlainText();
      }
    },
    copyPlainText() {
      try {
        const textToCopy = this.rawContent || this.extractTextFromHTML(this.content);
        navigator.clipboard.writeText(textToCopy);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2000);
      } catch (err) {
        console.error('Failed to copy plain text:', err);
      }
    },
    extractTextFromHTML(html) {
      // Create a temporary div to parse HTML and extract text
      const temp = document.createElement('div');
      temp.innerHTML = html;
      return temp.textContent || temp.innerText || '';
    }
  }
}
</script>

<style scoped>
.message-wrapper {
  display: flex;
  margin-bottom: 4px;
}

.message-right {
  justify-content: flex-end;
}

.message-left {
  justify-content: flex-start;
}

.message-bubble {
  max-width: 80%;
  border-radius: 12px;
  padding: 6px 10px;
  display: inline-block;
  word-wrap: break-word;
}

/* Add subtle border for assistant messages (left side) */
.message-left .message-bubble.theme--dark {
  border: 1px solid rgba(255, 255, 255, 0.12);
}

.message-left .message-bubble.theme--light {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.message-content {
  white-space: pre-wrap;
  word-wrap: break-word;
}

.user-content {
  color: white;
}

/* Override white-space for markdown content to prevent unwanted line breaks */
.markdown-content {
  white-space: normal;
}

/* Theme will be handled by inline style */

.message-footer {
  display: flex;
  align-items: center;
  margin-top: 4px;
  font-size: 10px;
  opacity: 0.5;
  gap: 8px;
}

.copy-btn {
  opacity: 0.7;
  transition: opacity 0.2s;
}

.copy-btn:hover {
  opacity: 1;
}

.message-bubble:hover .copy-btn {
  opacity: 0.7;
}

.message-right .message-footer {
  justify-content: flex-end;
}

.message-left .message-footer {
  justify-content: flex-start;
}

.message-time {
  white-space: nowrap;
}

.message-tokens {
  white-space: nowrap;
}

/* Markdown styling for assistant messages */
.markdown-content {
  line-height: 1.5;
}

.markdown-content >>> p {
  margin: 0 0 8px 0;
  line-height: 1.5;
}

.markdown-content >>> p:last-child {
  margin-bottom: 0;
}

.markdown-content >>> p:first-child {
  margin-top: 0;
}

/* Reduce spacing for consecutive paragraphs */
.markdown-content >>> p + p {
  margin-top: 0;
  margin-bottom: 4px;
}

/* Tighter spacing around strong/bold text */
.markdown-content >>> strong {
  font-weight: 600;
}

/* Dark theme code blocks */
.theme--dark .markdown-content >>> code {
  background: rgba(0, 0, 0, 0.3);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
}

.theme--dark .markdown-content >>> pre {
  background: rgba(0, 0, 0, 0.4);
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

/* Light theme code blocks */
.theme--light .markdown-content >>> code {
  background: rgba(0, 0, 0, 0.08);
  padding: 2px 6px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .markdown-content >>> pre {
  background: rgba(0, 0, 0, 0.06);
  padding: 12px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

.markdown-content >>> pre code {
  background: none;
  padding: 0;
}

.markdown-content >>> ul,
.markdown-content >>> ol {
  margin: 4px 0;
  padding-left: 20px;
}

.markdown-content >>> li {
  margin: 1px 0;
  line-height: 1.4;
}

.markdown-content >>> li p {
  margin: 0;
  display: inline-block;
}

/* When a list item starts with just a paragraph (common pattern), keep it inline */
.markdown-content >>> li > p:first-child:last-child {
  display: inline;
}

/* When paragraph is followed by a nested list, keep them flowing together */
.markdown-content >>> li > p:first-child {
  display: inline;
}

/* Reduce spacing for nested lists */
.markdown-content >>> li > ul,
.markdown-content >>> li > ol {
  margin: 1px 0;
  margin-top: 2px;
}

/* Dark theme blockquote */
.theme--dark .markdown-content >>> blockquote {
  border-left: 3px solid rgba(255, 255, 255, 0.3);
  padding-left: 12px;
  margin: 8px 0;
  opacity: 0.8;
}

/* Light theme blockquote */
.theme--light .markdown-content >>> blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.3);
  padding-left: 12px;
  margin: 8px 0;
  opacity: 0.7;
}

.markdown-content >>> a {
  color: var(--v-primary-lighten2);
  text-decoration: underline;
}

.markdown-content >>> h1,
.markdown-content >>> h2,
.markdown-content >>> h3,
.markdown-content >>> h4,
.markdown-content >>> h5,
.markdown-content >>> h6 {
  margin: 10px 0 6px 0;
  font-weight: 600;
  line-height: 1.3;
}

.markdown-content >>> h1:first-child,
.markdown-content >>> h2:first-child,
.markdown-content >>> h3:first-child,
.markdown-content >>> h4:first-child {
  margin-top: 0;
}

.markdown-content >>> h1 { font-size: 1.4em; }
.markdown-content >>> h2 { font-size: 1.3em; }
.markdown-content >>> h3 { font-size: 1.2em; }
.markdown-content >>> h4 { font-size: 1.1em; }

/* Table wrapper for horizontal scrolling */
.markdown-content >>> .table-wrapper {
  overflow-x: auto;
  margin: 12px 0;
  border-radius: 6px;
}

.theme--dark .markdown-content >>> .table-wrapper {
  background: rgba(0, 0, 0, 0.2);
}

.theme--light .markdown-content >>> .table-wrapper {
  background: rgba(0, 0, 0, 0.03);
}

/* Table styling - common */
.markdown-content >>> table {
  border-collapse: collapse;
  width: 100%;
  min-width: 400px;
  font-size: 12px;
}

/* Dark theme tables */
.theme--dark .markdown-content >>> table {
  color: rgba(255, 255, 255, 0.9);
}

.theme--dark .markdown-content >>> table th {
  background: rgba(255, 255, 255, 0.15);
  padding: 10px 12px;
  text-align: left;
  border: 1px solid rgba(255, 255, 255, 0.25);
  font-weight: 600;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.95);
}

.theme--dark .markdown-content >>> table td {
  padding: 8px 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  vertical-align: top;
}

.theme--dark .markdown-content >>> table tbody tr:nth-child(odd) {
  background: rgba(255, 255, 255, 0.05);
}

.theme--dark .markdown-content >>> table tbody tr:nth-child(even) {
  background: rgba(255, 255, 255, 0.02);
}

.theme--dark .markdown-content >>> table tbody tr:hover {
  background: rgba(255, 255, 255, 0.08);
  transition: background 0.2s ease;
}

/* Light theme tables */
.theme--light .markdown-content >>> table {
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .markdown-content >>> table th {
  background: rgba(0, 0, 0, 0.08);
  padding: 10px 12px;
  text-align: left;
  border: 1px solid rgba(0, 0, 0, 0.12);
  font-weight: 600;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.87);
}

.theme--light .markdown-content >>> table td {
  padding: 8px 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  vertical-align: top;
}

.theme--light .markdown-content >>> table tbody tr:nth-child(odd) {
  background: rgba(0, 0, 0, 0.02);
}

.theme--light .markdown-content >>> table tbody tr:nth-child(even) {
  background: rgba(0, 0, 0, 0.01);
}

.theme--light .markdown-content >>> table tbody tr:hover {
  background: rgba(0, 0, 0, 0.04);
  transition: background 0.2s ease;
}
</style>
